import React, { useState, useEffect, useContext } from "react";
import {
  Icon,
  Button,
  Form,
  Input,
  Checkbox,
  Dropdown,
} from "semantic-ui-react";
import {
  getAllPests,
  getAllPestsTreatment,
  updateUnits,
  getBuildingFloors,
} from "../../../../constants";
import { useToasts } from "react-toast-notifications";
import dateFormat from "dateformat";
import "./mdl-update-unit.css";
import { AppContext } from "../../../../Context/AppContext";

export default function MDL_UPDATE_UNIT({
  set_mdl_open,
  model_data,
  building_id,
  getBuildingData,
}) {
  /* VARIABLE DECLARATIONS
	---------------------------------------------------------------------------------*/
  const { userInfo, appVersion, isAppVersionOutdated } = useContext(AppContext);
  const [var_app_version] = appVersion;
  const [, set_is_app_version_outdated] = isAppVersionOutdated;
  const [var_add_unit, set_add_unit] = useState(model_data);
  const [form_validation_error, setFormError] = useState({});
  const [isChanged, setIsChanged] = useState(false);
  const [AllFloors, setAllFloors] = useState([]);
  const [user, setUser] = userInfo;

  const { addToast } = useToasts();

  /* EVENT LISTENERS
	---------------------------------------------------------------------------------*/

  useEffect(() => {
    getBuildingFloorsData();
  }, []);

  async function getBuildingFloorsData() {
    const requestHeaders = {
      headers: {
        entityid: user.uuid,
        appversion: var_app_version,
      },
    };
    await fetch(getBuildingFloors + building_id, requestHeaders)
      .then((response) => response.json())
      .then((res) => {
        if (res.code === 200) {
          setAllFloors(res.data);
        } else {
          if (res?.version && res?.version !== var_app_version) {
            set_is_app_version_outdated(true);
          }
        }
      });
  }

  /* EVENT FUNCTIONS
	---------------------------------------------------------------------------------*/
  const onChangeInput = (event) => {
    const { name, value } = event.target;

    // Clone the errors and clear error for current field
    const error_validation = { ...form_validation_error };
    if (error_validation[name]) {
      delete error_validation[name];
    }
    if (error_validation.error) {
      delete error_validation.error;
    }

    // Special validation for expiry_date
    if (name === "expiry_date") {
      const currentDate = new Date().toISOString().split("T")[0]; // yyyy-mm-dd
      const inputDate = value?.trim();

      if (
        !/^\d{4}-\d{2}-\d{2}$/.test(inputDate) ||
        inputDate < currentDate ||
        inputDate.slice(0, 4).length > 4
      ) {
        error_validation.expiry_date = "Invalid expiry date.";
      } else {
        delete error_validation.expiry_date;
      }
    }
    setIsChanged(true);
    set_add_unit({ ...var_add_unit, [name]: value });
    setFormError(error_validation);
  };

  const submitData = async () => {
    if (validated()) {
      var temp = {};
      temp.unit_uuid = var_add_unit.uuid;
      temp.unit = var_add_unit.unit;
      temp.bedroom_count = var_add_unit.bedroom_count;
      temp.floor = var_add_unit.floor;
      temp.town_house = var_add_unit.town_house;
      temp.user_id = user.user_id;
      const requestOptions = {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Token " + user.token,
          entityid: user.uuid,
          appversion: var_app_version,
        },
        body: JSON.stringify(temp),
      };
      await fetch(updateUnits + var_add_unit.uuid, requestOptions)
        .then((res) => res.json())
        .then((response) => {
          if (response.code && response.code === 401) {
            addToast("Session expired. Please Login again.", {
              appearance: "error",
              autoDismiss: true,
            });
            //SessionExpire();
            localStorage.removeItem("user");
            setUser(null);
          } else {
            if (response.code === 200) {
              addToast(response.success, {
                appearance: "success",
                autoDismiss: true,
              });
              getBuildingData(false);
              set_mdl_open(false);
            } else {
              if (response?.version && response?.version !== var_app_version) {
                set_is_app_version_outdated(true);
              }
              addToast(response.success, {
                appearance: "error",
                autoDismiss: true,
              });
            }
          }
        })
        .catch((error) => {
          addToast("There are some error. Try agin later", {
            appearance: "error",
            autoDismiss: true,
          });
        });
    }
  };

  function validated() {
    let return_value = true;
    let error_validation = { ...form_validation_error };

    const alphanumericRegex = /^[a-zA-Z0-9\s_-]+$/;

    const floor = var_add_unit.floor?.trim();
    const unit = var_add_unit.unit?.trim();
    const bedroom_count = var_add_unit.bedroom_count?.trim();

    // Validate Floor
    if (!floor) {
      error_validation.floor = "floor cannot be empty.";
      return_value = false;
    } else if (!alphanumericRegex.test(floor)) {
      error_validation.floor = "floor cannot contain special characters.";
      return_value = false;
    }

    // Validate Unit
    if (!unit) {
      error_validation.unit = "Please enter unit number.";
      return_value = false;
    } else if (!alphanumericRegex.test(unit)) {
      error_validation.unit = "Unit number cannot contain special characters.";
      return_value = false;
    }

    if (!bedroom_count) {
      error_validation.bedroom_count = "Please enter bedroom count.";
      return_value = false;
    } else if (!/^\d+$/.test(bedroom_count)) {
      error_validation.bedroom_count = "Bedroom count must be a number.";
      return_value = false;
    }

    setFormError(error_validation);
    return return_value;
  }

  const onChangeFloor = (e, { name, value }) => {
    set_add_unit({ ...var_add_unit, [name]: value });
    setIsChanged(true);
    const trimmed = value?.toString().trim();
    const isValid = /^[a-zA-Z0-9\s_-]+$/.test(trimmed) && trimmed !== "";

    setFormError((prev) => ({
      ...prev,
      floor: isValid ? null : "Please select a valid floor.",
    }));
  };

  /* RENDER APP
	---------------------------------------------------------------------------------*/
  return (
    <>
      <Form onSubmit={submitData}>
        <div className="modal-header">
          <div className="modal-header-title">Update Unit</div>
          <div
            className="modal-header-close"
            onClick={() => set_mdl_open(false)}
          >
            <Icon name="times circle outline" />
          </div>
        </div>
        <div className="modal-content">
          <Form.Group>
            <Form.Field
              id="unit"
              name="unit"
              label="Unit #"
              type="text"
              value={var_add_unit.unit}
              disabled={true}
              control={Input}
            />
          </Form.Group>
          <Form.Group>
            <Form.Field
              id="bedroom_count"
              name="bedroom_count"
              label="Bedroom Count"
              type="text"
              value={var_add_unit.bedroom_count}
              required
              control={Input}
              onChange={onChangeInput}
              error={
                form_validation_error.bedroom_count
                  ? {
                      content: form_validation_error.bedroom_count,
                      pointing: "above",
                    }
                  : null
              }
            />
          </Form.Group>
          <Form.Group>
            <Checkbox
              checked={var_add_unit.manual_floor === "1"}
              toggle
              onChange={(e, { checked }) => {
                checked
                  ? set_add_unit({
                      ...var_add_unit,
                      manual_floor: "1",
                      floor: "",
                    })
                  : set_add_unit({ ...var_add_unit, manual_floor: "0" });
                setIsChanged(true);
              }}
              label="Manual Floor"
            />
          </Form.Group>
          <Form.Group>
            {var_add_unit.manual_floor === "1" && (
              <Form.Field error={!!form_validation_error.floor}>
                <label>Manual Floor:</label>
                <Input
                  type="text"
                  value={var_add_unit.floor}
                  onChange={(e) => {
                    const value = e.target.value;
                    set_add_unit({ ...var_add_unit, floor: value });

                    const trimmed = value.trim();
                    const isValid =
                      /^[a-zA-Z0-9\s_-]+$/.test(trimmed) && trimmed !== "";

                    setFormError((prev) => ({
                      ...prev,
                      floor: isValid ? null : "floor cannot be empty.",
                    }));
                    setIsChanged(true);
                  }}
                />
                {form_validation_error.floor && (
                  <span style={{ color: "red", fontSize: "0.9em" }}>
                    {form_validation_error.floor}
                  </span>
                )}
              </Form.Field>
            )}
            {var_add_unit.manual_floor !== "1" && (
              <Form.Select
                label="Floors"
                placeholder="Select floor"
                fluid
                required
                value={var_add_unit.floor}
                onChange={onChangeFloor}
                name="floor"
                error={form_validation_error.floor}
                options={AllFloors}
              />
            )}
          </Form.Group>
          <Form.Group>
            <Form.Field>
              <Checkbox
                checked={var_add_unit.town_house === 1}
                toggle
                onChange={(e, { checked }) => {
                  checked
                    ? set_add_unit({ ...var_add_unit, town_house: 1 })
                    : set_add_unit({ ...var_add_unit, town_house: 0 });
                  setIsChanged(true);
                }}
                label="Townhouse"
              />
            </Form.Field>
          </Form.Group>
        </div>

        <div className="modal-footer">
          <Button disabled={!isChanged} type="submit" color="red">
            Submit
          </Button>
        </div>
      </Form>
    </>
  );
}
